import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { LogoLockup } from "@/components/Logo";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#0A0A0A]">
      {/* Left immersive panel */}
      <div className="relative hidden md:block overflow-hidden border-r border-white/10">
        <img
          src="https://images.unsplash.com/photo-1764258560214-71d5f28852d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG5lb24lMjBuZXVyYWwlMjBuZXR3b3JrJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgyMDQ5MTYwfDA&ixlib=rb-4.1.0&q=85"
          alt="Abstract neural network"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <LogoLockup mark="w-9 h-9" text="text-2xl" />
          </div>
          <div>
            <h2 className="font-heading font-extrabold text-4xl leading-tight tracking-tighter text-white">
              Your all-in-one <br />
              <span className="text-[#00FF66]">coding co-pilot.</span>
            </h2>
            <p className="mt-4 text-zinc-400 max-w-md leading-relaxed">
              Build websites, apps, backends and AI agents. Powered by Claude, GPT & Gemini —
              free for everyone.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 nig-grid-bg">
        <div className="w-full max-w-sm">
          <Link
            to="/"
            data-testid="login-back-home"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="md:hidden flex items-center gap-2 mb-8">
            <LogoLockup mark="w-9 h-9" text="text-2xl" />
          </div>

          <h1 className="font-heading font-bold text-3xl tracking-tighter text-white">
            {mode === "login" ? "Welcome back" : "Create account"}
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            {mode === "login"
              ? "Sign in to continue building."
              : "Start building for free — no limits."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="text-xs text-zinc-400 mb-1.5 block">Name</label>
                <Input
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace"
                  required
                  className="bg-[#141415] border-white/10 text-white h-11 focus-visible:ring-[#00FF66]"
                />
              </div>
            )}
            <div>
              <label className="text-xs text-zinc-400 mb-1.5 block">Email</label>
              <Input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-[#141415] border-white/10 text-white h-11 focus-visible:ring-[#00FF66]"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1.5 block">Password</label>
              <Input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-[#141415] border-white/10 text-white h-11 focus-visible:ring-[#00FF66]"
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold rounded-lg transition-colors duration-200 group"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  {mode === "login" ? "Sign in" : "Create account"}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              )}
            </Button>
          </form>

          <p className="mt-6 text-sm text-zinc-500 text-center">
            {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-mode-toggle"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-[#00FF66] hover:text-[#00CC52] font-medium transition-colors"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
